/**
 * 품질검사 규칙 제안 — 표본 값을 프로파일링해 규칙을 고른다. **LLM 없이 결정적으로** 동작한다.
 *
 * 제안은 표본에서 관측한 사실만 근거로 삼는다. 표본이 작으면 우연히 성립한 성질을 규칙으로
 * 굳혀 버리기 쉬우므로, 근거(evidence)를 함께 돌려주고 화면이 그대로 보여 준다.
 * 규칙 모양은 backend task_functions.quality_check 의 `{column, rule, param, message}` 와 같다.
 */
import type { Column } from '../utils/transformLoad';
import { parseDataType, profileValues } from './typeMeta';
import i18n from '../../../i18n';

const tr = (key: string, opts?: Record<string, unknown>) => String(i18n.t(`property:suggest.${key}`, opts as any));

export type QualityRuleKind = 'not_null' | 'unique' | 'range' | 'length' | 'in_list' | 'regex';

export interface RuleSuggestion {
  column: string;
  rule: QualityRuleKind;
  param: string;
  message: string;
  /** 왜 이 규칙을 제안했는지 — 사용자가 표본의 우연인지 판단할 수 있게 */
  evidence: string;
  /** 표본이 적어 확신이 낮은 제안 */
  weak: boolean;
}

/** 이 개수보다 표본이 적으면 어떤 제안도 '확신 낮음'으로 본다 */
const ENOUGH_ROWS = 20;
/** 코드값으로 볼 서로 다른 값의 최대 개수 */
const MAX_CODE_VALUES = 10;

const RULE_KINDS: QualityRuleKind[] = ['not_null', 'unique', 'range', 'length', 'in_list', 'regex'];

export function ruleLabel(kind: QualityRuleKind): string {
  return RULE_KINDS.includes(kind) ? tr(`rule.${kind}`) : kind;
}

/**
 * @param columns 검사 대상 항목 (상류 노드의 항목 정의)
 * @param rows 표본 행 — 없으면 타입 정의만으로 최소한의 제안을 낸다
 * @param existing 이미 정의된 규칙 — 같은 항목·같은 종류는 다시 제안하지 않는다
 */
export function suggestQualityRules(
  columns: Column[],
  rows: Array<Record<string, unknown>>,
  existing: Array<{ column?: string; rule?: string }> = [],
): RuleSuggestion[] {
  const have = new Set(existing.filter((r) => r.column && r.rule).map((r) => `${r.column}:${r.rule}`));
  const n = rows.length;
  const thin = n < ENOUGH_ROWS;
  const out: RuleSuggestion[] = [];
  const add = (s: RuleSuggestion) => { if (!have.has(`${s.column}:${s.rule}`)) out.push(s); };

  for (const col of columns) {
    const name = col.name;
    const type = parseDataType(col.dataType);
    const values = rows.map((r) => r[name]);
    const present = values.filter((v) => v !== undefined && v !== null && String(v).trim() !== '');
    const p = n ? profileValues(values) : null;

    // 1) 필수 — 표본에 빈 값이 하나도 없었다
    if (p && p.nullRate === 0 && n > 0) {
      add({
        column: name, rule: 'not_null', param: '',
        message: tr('q.notNullMsg', { name }),
        evidence: tr('q.notNullEv', { n }),
        weak: thin,
      });
    }

    // 2) 허용값 — 서로 다른 값이 몇 개뿐이다 (상태·구분 코드)
    const distinct = Array.from(new Set(present.map((v) => String(v))));
    if (present.length >= 5 && distinct.length > 0 && distinct.length <= MAX_CODE_VALUES
        && distinct.length <= Math.max(2, Math.floor(present.length / 2))) {
      add({
        column: name, rule: 'in_list', param: distinct.sort().join(','),
        message: tr('q.inListMsg', { name }),
        evidence: tr('q.inListEv', { n: present.length, k: distinct.length, values: `${distinct.slice(0, 5).join(', ')}${distinct.length > 5 ? '…' : ''}` }),
        weak: thin || distinct.length === present.length,
      });
    }

    // 3) 범위 — 숫자 항목이 표본에서 모두 0 이상이었다.
    //    관측 최댓값을 상한으로 삼지 않는다(다음 데이터가 더 클 수 있다) — 하한만 건다.
    const numericType = type.base === 'INTEGER' || type.base === 'NUMERIC';
    const nums = present.map((v) => Number(String(v).replace(/,/g, ''))).filter((x) => Number.isFinite(x));
    if ((numericType || (p && p.pattern.startsWith('number'))) && nums.length >= 3 && nums.every((x) => x >= 0)) {
      add({
        column: name, rule: 'range', param: '0,',
        message: tr('q.rangeMsg', { name }),
        evidence: tr('q.rangeEv', { n: nums.length, min: Math.min(...nums) }),
        weak: thin,
      });
    }

    // 4) 길이 — 자릿수가 하나로 고정된 코드 (사업자번호·우편번호 같은 것)
    if (p && p.pattern === 'code' && present.length >= 5) {
      const lens = Array.from(new Set(present.map((v) => String(v).length)));
      if (lens.length === 1) {
        add({
          column: name, rule: 'length', param: `${lens[0]},${lens[0]}`,
          message: tr('q.lengthMsg', { name, len: lens[0] }),
          evidence: tr('q.lengthEv', { len: lens[0] }),
          weak: thin,
        });
      }
    }

    // 5) 형식 — 날짜 문자열은 모양이 어긋나면 적재 시점에 터진다
    if (p && p.pattern.startsWith('date:') && type.base === 'VARCHAR') {
      const shape = p.pattern.slice(5);
      const re = shape === 'YYYYMMDD' ? '\\d{8}'
        : shape === 'YYYY-MM-DD' ? '\\d{4}-\\d{2}-\\d{2}'
          : shape === 'YYYY/MM/DD' ? '\\d{4}/\\d{2}/\\d{2}' : '';
      if (re) {
        add({
          column: name, rule: 'regex', param: re,
          message: tr('q.regexMsg', { name, shape }),
          evidence: tr('q.regexEv', { shape }),
          weak: thin,
        });
      }
    }

    // 6) 고유 — 표본에서 중복이 없었다. 표본이 작으면 우연이므로 확신을 낮춘다.
    if (present.length >= ENOUGH_ROWS && distinct.length === present.length && /(_id|no|번호|id)$/i.test(name)) {
      add({
        column: name, rule: 'unique', param: '',
        message: tr('q.uniqueMsg', { name }),
        evidence: tr('q.uniqueEv', { n: present.length }),
        weak: true,          // 표본만으로는 단정할 수 없다 — 늘 확인이 필요하다
      });
    }
  }
  return out;
}
